import { useState } from 'react'
import { useOperatorQueue } from '../hooks/useOperator.js'

// Operator Queue — the page Scott (or a future VA) lives on once a
// week to clear out approved managed-posting drafts.
//
// Each row shows the customer name, platform, post type, and full
// content (hook/caption/hashtags + shot list/slides if present). One
// "Mark as posted" button per row, with an optional notes field for
// "scheduled in Buffer at 9am Tuesday" style breadcrumbs.
//
// Gated upstream by App.jsx — this screen only mounts when the
// useIsOperator() hook returns true. RLS on the underlying RPC is
// the security backstop in case the gate ever falters.
export default function OperatorQueueScreen() {
  const { queue, loading, error, refresh, markAsPosted } = useOperatorQueue()
  const [busyId, setBusyId] = useState(null)
  const [notesById, setNotesById] = useState({})
  const [errMsg, setErrMsg] = useState(null)
  const [copied, setCopied] = useState(null)

  async function handleMark(draftId) {
    if (busyId) return
    setBusyId(draftId)
    setErrMsg(null)
    try {
      await markAsPosted(draftId, notesById[draftId] || null)
      setNotesById((n) => {
        const next = { ...n }
        delete next[draftId]
        return next
      })
    } catch (e) {
      setErrMsg(e.message || String(e))
    } finally {
      setBusyId(null)
    }
  }

  function handleCopy(draftId, payload) {
    if (!navigator?.clipboard) return
    navigator.clipboard.writeText(payload).then(() => {
      setCopied(draftId)
      setTimeout(() => setCopied((c) => (c === draftId ? null : c)), 1500)
    })
  }

  if (loading) {
    return <div className="px-4 py-8 text-sm text-black/55">Loading queue…</div>
  }

  if (error) {
    return (
      <div className="mx-4 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        Could not load queue: {error}
      </div>
    )
  }

  return (
    <div className="pb-24">
      <div className="mx-4 mt-4 flex items-baseline justify-between gap-2">
        <div>
          <h1 className="text-xl font-medium">Operator Queue</h1>
          <p className="text-xs text-black/55">
            {queue.length === 0
              ? 'Nothing approved and waiting.'
              : `${queue.length} approved post${queue.length === 1 ? '' : 's'} waiting to be published.`}
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="rounded-lg border border-black/15 bg-white px-3 py-1.5 text-sm text-black/65 hover:bg-black/5"
        >
          Refresh
        </button>
      </div>

      {errMsg && (
        <div className="mx-4 mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errMsg}
        </div>
      )}

      {queue.length === 0 ? (
        <div className="mx-4 mt-6 rounded-2xl border border-dashed border-black/15 bg-white px-4 py-10 text-center text-sm text-black/55">
          You're all clear. Approved drafts from managed-posting customers will appear here.
        </div>
      ) : (
        <ul className="mx-4 mt-4 space-y-4">
          {queue.map((row) => {
            const fullText = buildFullText(row)
            const noteVal = notesById[row.draft_id] || ''
            return (
              <li key={row.draft_id} className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{row.restaurant_name}</div>
                    <div className="mt-0.5 text-xs text-black/55">
                      <span className="font-medium">{row.platform}</span>
                      {' · '}
                      {row.post_type}
                      {row.tier_display_name && ` · ${row.tier_display_name}`}
                      {row.generated_for_week && ` · week of ${row.generated_for_week}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(row.draft_id, fullText)}
                    className="rounded-md border border-black/15 bg-white px-2.5 py-1 text-xs text-black/65 hover:bg-black/5"
                  >
                    {copied === row.draft_id ? 'Copied!' : 'Copy all'}
                  </button>
                </div>

                {row.hook && (
                  <div className="mt-3 rounded-lg bg-black/5 px-3 py-2">
                    <div className="text-[10px] font-medium text-black/55">Hook</div>
                    <div className="mt-0.5 text-sm font-medium">{row.hook}</div>
                  </div>
                )}

                <div className="mt-3">
                  <div className="text-[10px] font-medium text-black/55">Caption</div>
                  <div className="mt-0.5 whitespace-pre-wrap text-sm">{row.caption}</div>
                </div>

                {Array.isArray(row.hashtags) && row.hashtags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {row.hashtags.map((tag, i) => (
                      <span key={i} className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {Array.isArray(row.shot_list) && row.shot_list.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] font-medium text-black/55">Shot list</div>
                    <ol className="mt-0.5 list-decimal pl-5 text-sm space-y-1">
                      {row.shot_list.map((s, i) => (
                        <li key={i}>
                          <span className="font-mono text-xs text-black/55">{s.ts}</span> {s.shot}
                          {s.voice && <em className="ml-1 text-black/65">— "{s.voice}"</em>}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {Array.isArray(row.slides) && row.slides.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] font-medium text-black/55">Slides</div>
                    <ol className="mt-0.5 list-decimal pl-5 text-sm space-y-2">
                      {row.slides.map((s, i) => (
                        <li key={i}>
                          <div className="font-medium">{s.headline}</div>
                          <div className="text-black/65">{s.body}</div>
                          <div className="text-xs italic text-black/55">image: {s.image_idea}</div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                <div className="mt-4 border-t border-black/5 pt-3">
                  <label className="text-[10px] font-medium text-black/55">
                    Operator notes (optional)
                  </label>
                  <input
                    type="text"
                    value={noteVal}
                    onChange={(e) => setNotesById((n) => ({ ...n, [row.draft_id]: e.target.value }))}
                    placeholder="e.g., scheduled in Buffer for Tue 9am"
                    className="mt-1 w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                  />
                  <button
                    type="button"
                    disabled={busyId === row.draft_id}
                    onClick={() => handleMark(row.draft_id)}
                    className="mt-2 w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {busyId === row.draft_id ? 'Marking…' : 'Mark as posted'}
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// Build a clipboard-friendly version of the post — caption + hashtags
// concatenated. Operator pastes it into Buffer/IG/etc. as-is.
function buildFullText(row) {
  const parts = []
  if (row.caption) parts.push(row.caption)
  if (Array.isArray(row.hashtags) && row.hashtags.length > 0) {
    parts.push('')
    parts.push(row.hashtags.join(' '))
  }
  return parts.join('\n')
}
